import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import type { ProductChapter } from "./ProductDetail";

export const CHAPTER_LABELS: Record<ProductChapter | "facts" | "closing", string> = {
  overview: "Sobre el producto",
  metadata: "Ficha técnica",
  pillars: "Pilares",
  ingredients: "Ingredientes",
  specTable: "Composición",
  stats: "En cifras",
  process: "Mecanismo",
  sideBySide: "Comparativo",
  facts: "Datos del producto",
  closing: "Comprar",
};

export function chapterAnchor(chapter: string): string {
  return `capitulo-${chapter}`;
}

/**
 * La navegación por capítulos, pegada bajo la barra: una fila de píldoras
 * que lleva a cada bloque de la ficha y marca en cuál se está. La ficha
 * de producción numera sus bloques pero no ofrece forma de saltar a
 * ellos; con seis o siete secciones de una pantalla cada una, el índice
 * es lo que convierte la ficha en algo navegable en vez de un rollo.
 *
 * Solo enseña los capítulos que ESTE producto tiene (`chapters` viene de
 * `chaptersOf`, la única lista de qué se pinta).
 */
export function ProductChapterNav({ chapters }: { chapters: readonly string[] }) {
  const { t } = useTranslation();
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (chapters.length === 0 || typeof IntersectionObserver === "undefined") return;
    const targets = chapters
      .map((chapter) => document.getElementById(chapterAnchor(chapter)))
      .filter((el): el is HTMLElement => Boolean(el));
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [chapters]);

  if (chapters.length < 2) return null;

  return (
    <nav
      aria-label={t("Capítulos de la ficha")}
      className="sticky top-[92px] z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
    >
      <ul className="flex gap-2 overflow-x-auto rounded-full border border-ds-border bg-white/85 p-1.5 shadow-ds-card backdrop-blur-xl [scrollbar-width:none]">
        {chapters.map((chapter, index) => {
          const id = chapterAnchor(chapter);
          const isActive = active === id;
          return (
            <li key={chapter} className="shrink-0">
              <a
                href={`#${id}`}
                aria-current={isActive ? "location" : undefined}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition",
                  isActive
                    ? "bg-ds-ink text-white"
                    : "text-ds-ink-3 hover:bg-ds-lavender-bg hover:text-ds-ink",
                )}
              >
                <span className={cn("font-mono text-[10px]", isActive ? "text-white/70" : "text-ds-violet-pop")}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                {t(CHAPTER_LABELS[chapter as keyof typeof CHAPTER_LABELS] ?? chapter)}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
